import React from 'react';
import { PropTypes } from 'prop-types';
import { setClassNames } from 'ddc-classnames-js';
import ContactButtons from './ContactButtons';
import LocationDetails from './LocationDetails';
import LocationHours from './LocationHours';
import LocationMapLinks from './LocationMapLinks';
import LocationMap from './LocationMap';
import MapErrorBoundary from './MapErrorBoundary';
import { buildFullAddress } from '../utils/buildFullAddress';
import useIntersectionObserver from '../hooks/useIntersectionObserver';

const Location = ({
	address,
	city,
	state,
	postalCode,
	phones,
	hours,
	timeZoneId,
	emailAddress,
	isAccordion
}) => {
	const [mapRef, isVisible] = useIntersectionObserver({
		root: null, // viewport as the root
		rootMargin: '0px',
		threshold: 0.1 // 10% of the component must be visible
	});
	const wrapperClasses = [
		'location',
		'd-sm-flex',
		'w-100',
		'pb-6',
		isAccordion ? 'mt-3' : 'mt-7',
		isAccordion ? '' : 'border-bottom'
	];
	const fullAddress = buildFullAddress({
		address,
		city,
		state,
		postalCode
	});
	// Static map URL as fallback and for map link, opens in new tab
	const staticMapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
		fullAddress
	)}`;
	return (
		<section className={setClassNames(wrapperClasses)}>
			<section className="location-info-wrapper">
				<ContactButtons
					phoneNumber={phones[0]?.number}
					emailAddress={emailAddress}
				/>
				<LocationDetails
					address={address}
					city={city}
					state={state}
					postalCode={postalCode}
					phones={phones}
				/>
				<LocationHours hours={hours} timeZoneId={timeZoneId} />
				<LocationMapLinks
					mapUrl={staticMapUrl}
					fullAddress={fullAddress}
				/>
			</section>
			<section className="location-map-wrapper" ref={mapRef}>
				{isVisible && (
					<MapErrorBoundary mapUrl={staticMapUrl}>
						<LocationMap
							address={address}
							city={city}
							state={state}
							postalCode={postalCode}
							mapUrl={staticMapUrl}
						/>
					</MapErrorBoundary>
				)}
			</section>
		</section>
	);
};

Location.propTypes = {
	address: PropTypes.string.isRequired,
	city: PropTypes.string.isRequired,
	state: PropTypes.string.isRequired,
	postalCode: PropTypes.string.isRequired,
	emailAddress: PropTypes.string,
	timeZoneId: PropTypes.string.isRequired,
	isAccordion: PropTypes.bool.isRequired,
	phones: PropTypes.arrayOf(
		PropTypes.shape({
			type: PropTypes.string.isRequired,
			number: PropTypes.string.isRequired
		})
	).isRequired,
	hours: PropTypes.shape({
		monday: PropTypes.string.isRequired,
		tuesday: PropTypes.string.isRequired,
		wednesday: PropTypes.string.isRequired,
		thursday: PropTypes.string.isRequired,
		friday: PropTypes.string.isRequired,
		saturday: PropTypes.string.isRequired,
		sunday: PropTypes.string.isRequired
	}).isRequired
};

export default Location;
